import { message, interstitialAdType } from '../config';
import Base from './Base';
import Game from './Game';
import Header from './Header';
import FullScreenAd from './FullScreenAd';
import Result from './Result';

/**
 * Main
 */
export default class Main extends Base {
    /**
     * constructor
     */
    constructor() {
        super({
            el: window.document,
            wrapperEl: '.js-wrapper__contents',
            landscapeEl: '.js-game-play__landscape',
        });
    }

    /**
     * initialize
     */
    initialize() {
        /** @property {Header} header ヘッダ */
        this.header = new Header();

        /** @property {Game} game ゲーム */
        this.game = new Game();

        /** @property {Result} result リザルト */
        this.result = new Result();

        /** @property {object} fullscreenAds インステ広告 */
        this.fullscreenAds = this.getFullscreenAds();

        this.binds();
    }

    /**
     * インステ広告のオブジェクトを取得する
     *
     * @return {object}
     */
    getFullscreenAds() {
        const fullscreenAds = {};

        Object.keys(this.config.adparam).forEach((type) => {
            const { adClass } = this.config.adparam[type];

            if (!interstitialAdType.includes(type) || adClass === 'none') {
                return;
            }

            fullscreenAds[type] = new FullScreenAd({ type });
        });

        return fullscreenAds;
    }

    /**
     * イベントリスナー登録
     */
    binds() {
        window.addEventListener('orientationchange', e => this.onOrientationchange(e));
        window.addEventListener('visibilitychange', e => this.onVisibilitychange(e));
        window.addEventListener('message', e => this.onMessage(e));
    }

    /**
     * View間のやりとりを行うためのバインドを行う
     * 別のViewのメソッドに直接アクセスするのはここだけ
     */
    observe() {
        const { observer } = this;

        // ランドスケープ時
        observer.on(message.windowLandscape, (e) => {
            this.game.onWindowLandscape(e);
            this.onWindowLandscape(e);
        });

        // ポートレート時
        observer.on(message.windowPortrait, async (e, isManual = false) => {
            this.onWindowPortrait(e);

            if (this.game.isReady()) {
                await this.device.initScreenSize();
            } else {
                await this.device.setScreenSize();
            }

            // ratioの再計算が必要な場合
            if (typeof this.config.recalculateRatio === 'function') {
                this.config.ratio = this.config.recalculateRatio.apply(this, [this.config.ratio]);
                delete this.config.recalculateRatio;
            }

            // ゲーム画面が表示された時にゲーム中広告がなければ埋めるための処理
            const beforeLoadCallback = () => {
                // 表示されているインステ広告があるか
                const isVisibleFullscreenAd = Object.keys(this.fullscreenAds)
                    .some(key => this.fullscreenAds[key].isVisible());

                if (!isManual && !isVisibleFullscreenAd && this.game.canLoadingAd()) {
                    this.game.loadReloadableAd();
                }
            };

            this.game.onWindowPortrait(e, {
                effectiveHeight: this.device.height - this.header.offsetHeight,
                beforeLoadCallback,
            });
        });

        // ゲームロード時
        observer.on(message.gameLoad, () => {
            this.game.onLoad();
        });

        // ゲーム: スタートクリック時
        observer.on(message.gameClickStart, (e) => {
            this.game.onStartClick(e);
        });

        // ゲーム: ゲーム開始時
        observer.on(message.gameStart, (e, payload) => {
            this.game.onGameStart(e, payload);
        });

        // ゲーム: ゲームリワード時
        observer.on(message.gameReward, () => {
            this.game.stopAdReload();
            this.header.hide();
            this.fullscreenAds.reward.show();
            this.hideNoticeWhenLandscape();
        });

        // ゲーム: ゲーム開始前時
        observer.on(message.gameReady, () => {
            if (this.fullscreenAds.ready) {
                this.game.stopAdReload();
                this.game.isDisplayReadyAd = true;
                this.header.hide();
                this.fullscreenAds.ready.show();
                this.hideNoticeWhenLandscape();
            }
        });

        // ゲーム: ゲーム終了時
        observer.on(message.gameEnd, (e, payload) => {
            this.game.onGameEnd(e, payload);
            this.header.show();
            this.result.show(payload);

            // ゲーム終了時、ゲーム領域を連打するとモーダル表示中に背面でゲームが再開してしまっているバグ対応
            // モーダルウィンドウを開いた時点で全画面広告を消す
            Object.keys(this.fullscreenAds).forEach((key) => {
                this.fullscreenAds[key].hide();
            });

            this.hideNoticeWhenLandscape();
        });

        // ゲーム: ゲームリトライ時
        observer.on(message.gameRetry, (e) => {
            this.game.onGameRetry(e);
        });

        // リトライボタンクリック時
        observer.on(message.resultClickRetry, (e, isShowAd) => {
            if (this.fullscreenAds.retry && isShowAd) {
                this.header.hide();
                this.fullscreenAds.retry.show();
                this.game.muteAd();
            } else {
                this.showNoticeWhenLandscape();
                this.game.postMessage(message.mainRetry, this.targetOrigin);
            }
        });

        // インタースティシャル広告スキップ時
        observer.on(message.fullscreenAdClickSkip, (e, type) => {
            this.game.unmuteAd();
            this.header.show();
            this.showNoticeWhenLandscape();
            this.game.postMessage(`habits:${type}`, this.targetOrigin);

            if (type === 'retry') {
                return;
            }

            this.game.loadReloadableAd();
        });

        // 初期表示はランディング時の画面の向きによって異なる
        const orientation = this.device.isLandscape() ? message.windowLandscape : message.windowPortrait;
        observer.emit(orientation, true);
    }

    /**
     * メッセージ受信時
     *
     * @param {Event} e イベント
     */
    onMessage(e) {
        // 送信元を確認
        if (e.origin !== this.config.targetOrigin || !e.data) {
            return;
        }

        let json = {};
        try {
            json = JSON.parse(e.data);
        } catch (err) {
            // 想定外のmessage
            return;
        }

        const { action, payload } = json;

        if (!action) {
            return;
        }

        if (!(Object.keys(message).some(key => message[key] === action))) {
            throw new Error(`Invalid action: ${action}`);
        }

        this.observer.emit(action, payload);
    }

    /**
     * ランドスケープとポートレートの切替時
     */
    async onOrientationchange() {
        await new Promise(resolve => this.device.awaitOrientationChange(resolve));
        const orientation = this.device.isLandscape() ? message.windowLandscape : message.windowPortrait;
        this.observer.emit(orientation);
    }

    /**
    * ウィンドウの表示状態の切替時
    */
    async onVisibilitychange() {
        if (document.visibilityState !== 'visible') {
            return;
        }

        if (this.device.isLandscape(window)) {
            return;
        }

        this.observer.emit(message.windowPortrait);
    }

    /**
     * ランドスケープ時
     */
    onWindowLandscape() {
        this.$landscapeEl.style.display = 'block';
    }

    /**
     * ポートレート時
     */
    onWindowPortrait() {
        this.$landscapeEl.style.display = 'none';
    }

    /**
     * ランドスケープにしてもNoticeを常に表示しない
     */
    hideNoticeWhenLandscape() {
        this.$wrapperEl.classList.add('-modal');
    }

    /**
     * ランドスケープにするとNoticeを表示する
     */
    showNoticeWhenLandscape() {
        this.$wrapperEl.classList.remove('-modal');
    }
}
